import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../common/components";
import { routes } from "../../../constants/routes";
import images from "../../../assets/images/image";
import "./tenant-details.scss";
import { Box } from "@mui/material";
import RightTenantDetailsPanel from "./tenant-details-right-panel/tenant-details-right-panel";
import { getTenantDetails, getTenetRoles } from "../../../api/tenets";
import { useSetLoading } from "../../../common/hooks/appearance";
import _ from "lodash";
import { AlertMessage, TenantData } from "./tenant-details-types";
import TenantDetailsLeftPanel from "./tenant-details-left-panel/tenant-details-left-panel";
import { ROLES } from "../../../utils/constants";

const TenantDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { partnerDetails, defaultTabIndex } = state || {};
  const tenantId = partnerDetails?.id;

  const [tenantData, setTenantData] = React.useState<TenantData | null>(null);
  const [tenetRoles, setTenetRoles] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });

  const fetchTenantData = async () => {
    if (!tenantId) return;

    try {
      setIsLoading(true);
      const response = await getTenantDetails({ id: tenantId });
      setIsLoading(false);
      setTenantData(response?.data?.data);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching tenant details:", error);
    }
  };

  const getRole = async () => {
    try {
      setIsLoading(true);
      const response: any = await getTenetRoles({ tenantId: tenantId });
      setIsLoading(false);
      if (response?.data?.success) {
        const roleOptions = response?.data?.data?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.role,
            disabled: item?.role === ROLES.SUPER_ADMIN,
          };
        });

        setTenetRoles(roleOptions);
      }
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "some thing went wrong!",
        type: "error",
      });
      console.error(error);
    }
  };

  useEffect(() => {
    getRole();
  }, []);

  useEffect(() => {
    fetchTenantData();
  }, [tenantId, partnerDetails]);

  const breadcrumbLink = [
    {
      label: "Partner List",
      link: routes.PARTNERLIST,
    },
    {
      label:
        partnerDetails?.company_name || tenantData?.basic_details?.company_name,
      handleClick: () => {},
    },
  ];

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box className="tenant-details-wrapper">
      <Box className="breadcrumb-wrapper">
        <BreadcrumbComponent
          breadcrumbLink={breadcrumbLink}
          showBackArrow
          handleBackArrowClick={() => navigate(routes.PARTNERLIST)}
        />
      </Box>
      <Box className="tenant-details-content">
        <Box className="tenant-details-bg">
          <img
            alt="Bg-tenant-details"
            src={images.BgTenantDetails}
            className="responsive-image"
          />
        </Box>
        <Box className="tenant-details-container">
          <Box className="tenant-details-panels-wrapper" gap={2}>
            <TenantDetailsLeftPanel
              tenantData={tenantData}
              tenetRoles={tenetRoles}
              setTenantData={setTenantData}
              partnerDetails={partnerDetails}
              tenantId={tenantId}
            />
            <Box className="tenant-details-right-panel">
              <RightTenantDetailsPanel
                tenantData={tenantData}
                tenetRoles={tenetRoles}
                setTenantData={setTenantData}
                partnerDetails={partnerDetails}
                defaultTabIndex={defaultTabIndex}
              />
            </Box>
          </Box>
        </Box>
        {alertMessage.open && (
          <AlertComponent
            message={alertMessage.message}
            type={alertMessage.type || "info"}
            open={alertMessage.open}
            onClose={() => setAlertMessage({ open: false })}
          />
        )}
      </Box>
    </Box>
  );
};

export default TenantDetails;
